import Head from "next/head";
import { StaticPageLayout } from "@/components/StaticPageLayout";
import { Box, Container, Title, rem } from "@mantine/core";
import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { Layout } from "@/components/Layout";
import { FaqAccordion } from "@/components/FaqAccordion";
import { useRouter } from "next/router";
import BreadcrumbListJsonLd from "@/components/JsonLd/BreadcrumbList";
import WebpageJsonLd from "@/components/JsonLd/WebPage";
import FaqPageJsonLd from "@/components/JsonLd/FaqPage";

interface IQuestionContents {
  title: string;
  contents: [{ question: string; answer: string[] }];
}

export default function FAQ(): JSX.Element {
  const { t, ready } = useTranslation(["faq"]);
  const router = useRouter();

  const breadcrumbs = [
    { title: t("common:service.title"), href: "/" },
    { title: t("common:links.faq"), href: "/faq" },
  ];

  /**
   * For any changes/adjustments on FAQ texts, refer to the
   * faq.json files under whichever locale you want to edit.
   *
   * Ex. /public/locales/en/faq.json
   */
  const items = t("faq:contents", {
    returnObjects: true,
  }) as IQuestionContents[];

  return (
    <>
      <Head>
        <title>{t("meta:faq.title")}</title>
        <meta content={t("meta:faq.description")} name="description" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <link href="/favicon.ico" rel="icon" />
      </Head>
      <Layout footer={{ breadcrumbs: breadcrumbs }}>
        <BreadcrumbListJsonLd breadcrumbs={breadcrumbs} />
        <WebpageJsonLd
          name={t("meta:faq.title")}
          description={t("meta:faq.description")}
          pathname={router.pathname}
        />
        <FaqPageJsonLd items={items.flatMap((section) => section.contents)} />
        <StaticPageLayout title={t("common:links.faq")} />
        <Container size="xl" mb={rem(52)}>
          {ready &&
            items?.map((item: IQuestionContents, faqIndex) => (
              <Box key={item.title} mt={rem(52)}>
                <Title
                  order={2}
                  size={rem(20)}
                  c="blue.9"
                  h={rem(28)}
                  lh="xs"
                  mb="md"
                  className={`border border-solid border-l-[5px] border-r-0 border-t-0 
                      border-b-0 px-3`}
                >
                  {item.title}
                </Title>
                <FaqAccordion item={item} faqIndex={faqIndex} />
              </Box>
            ))}
        </Container>
      </Layout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  const translations = await serverSideTranslations(locale ?? "en", [
    "meta",
    "common",
    "faq",
  ]);

  return {
    props: {
      ...translations,
    },
  };
};
